/**
 * Per-demand activity history (local fallback when API/D1 is unavailable).
 * Server of record is Matter_Events in D1 when migrations are applied.
 */

import { disputeLabel } from './disputeTypes.js';
import { installmentPlanLabel } from './installmentPlans.js';
import { settlementChoiceHistoryLabel } from './settlementChoice.js';
import { debtorCountryLabel, normalizeDebtorCountry } from './debtorCountry.js';
import { evidenceFileKey } from './evidenceFiles.js';
import { formatMatterMoney } from './matterCurrency.js';

const KEY = 'demandjet.matterHistory';

export const HISTORY_EVENT_TYPES = Object.freeze({
  CREATED: 'CREATED',
  RECORD_UPDATED: 'RECORD_UPDATED',
  EVIDENCE_UPDATED: 'EVIDENCE_UPDATED',
  MESSAGE_SENT: 'MESSAGE_SENT',
  MESSAGE_DELETED: 'MESSAGE_DELETED',
  ARCHIVED: 'ARCHIVED',
  RESTORED: 'RESTORED',
  STATUS_CHANGED: 'STATUS_CHANGED',
  ATTORNEY_ASSIGNED: 'ATTORNEY_ASSIGNED',
});

const TRACKED_FIELDS = Object.freeze([
  { key: 'Debtor_Company', label: 'company' },
  { key: 'Debtor_Name', label: 'contact name' },
  { key: 'Debtor_Country', label: 'country', kind: 'country' },
  { key: 'Debtor_Street', label: 'street' },
  { key: 'Debtor_City', label: 'city' },
  { key: 'Debtor_State', label: 'state' },
  { key: 'Debtor_Postal_Code', label: 'postal code' },
  { key: 'Debtor_Email', label: 'email' },
  { key: 'Amount_Owed', label: 'amount owed', kind: 'amount' },
  { key: 'Currency', label: 'currency' },
  { key: 'External_Invoice_ID', label: 'invoice ID' },
  { key: 'External_Account_Number', label: 'account #' },
  { key: 'Due_Date', label: 'due date' },
  { key: 'Tracking_Number', label: 'tracking number' },
  { key: 'Dispute_Type', label: 'dispute type', kind: 'dispute' },
  { key: 'Installment_Plan', label: 'payment plan', kind: 'plan' },
  { key: 'Settlement_Choice', label: 'settlement', kind: 'settlement' },
  { key: 'Evidence_Vault_URL', label: 'evidence', kind: 'evidence' },
]);

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  localStorage.setItem(KEY, JSON.stringify(all));
}

function blank(value) {
  if (value == null) return '';
  return String(value).trim();
}

function normalizeComparable(value, kind, context = {}) {
  if (kind === 'amount') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '';
  }
  if (kind === 'plan') {
    return blank(value).toUpperCase() || 'NO';
  }
  if (kind === 'settlement') {
    // Missing matches the form default so older demands do not look changed.
    return blank(value).toUpperCase() === 'YES' ? 'YES' : 'NO';
  }
  if (kind === 'country') {
    // Treat missing Debtor_Country like the form does (infer from postal/state,
    // else USA) so email-only saves do not log a phantom "— → USA" country change.
    return normalizeDebtorCountry(value, {
      postalCode: context.postalCode,
      state: context.state,
    });
  }
  if (kind === 'evidence') {
    // Compare by file names so catalyst://…/file.pdf vs pending-upload://file.pdf
    // is not treated as an evidence change.
    return evidenceFileKey(value);
  }
  return blank(value);
}

function displayValue(value, kind, currency = 'USD') {
  const raw = blank(value);
  if (!raw && kind !== 'amount' && kind !== 'settlement') return '—';
  if (kind === 'amount') {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return formatMatterMoney(n, currency, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (kind === 'dispute') return disputeLabel(raw) || raw || '—';
  if (kind === 'country') return debtorCountryLabel(raw) || raw || '—';
  if (kind === 'plan') {
    const label = installmentPlanLabel(raw);
    if (label === 'Yes') return 'Accepts payment plan';
    if (label === 'No') return 'No payment plan';
    return label || '—';
  }
  if (kind === 'settlement') return settlementChoiceHistoryLabel(raw);
  if (kind === 'evidence') {
    if (!raw) return 'none';
    if (raw.startsWith('pending-upload://')) {
      const names = raw
        .slice('pending-upload://'.length)
        .split(',')
        .map((part) => {
          try {
            return decodeURIComponent(part.trim());
          } catch {
            return part.trim();
          }
        })
        .filter(Boolean);
      if (!names.length) return 'files pending';
      if (names.length === 1) return names[0];
      return `${names.length} files`;
    }
    const parts = raw.split('/');
    return parts[parts.length - 1] || raw;
  }
  return raw;
}

function joinList(items) {
  if (!items.length) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * Build a detailed history summary for matter field changes.
 * @returns {{ type: string, summary: string, changes: Array<{key:string,label:string,from:string,to:string}> }}
 */
export function summarizeMatterChanges(before = {}, after = {}) {
  const changes = [];
  for (const field of TRACKED_FIELDS) {
    const prevRaw = before?.[field.key];
    const nextRaw = after?.[field.key];
    const prevCmp = normalizeComparable(prevRaw, field.kind, {
      postalCode: before?.Debtor_Postal_Code,
      state: before?.Debtor_State,
    });
    const nextCmp = normalizeComparable(nextRaw, field.kind, {
      postalCode: after?.Debtor_Postal_Code,
      state: after?.Debtor_State,
    });
    if (prevCmp === nextCmp) continue;
    const amountCurrency =
      field.kind === 'amount'
        ? after?.Currency || before?.Currency || 'USD'
        : 'USD';
    changes.push({
      key: field.key,
      label: field.label,
      from: displayValue(prevRaw, field.kind, amountCurrency),
      to: displayValue(nextRaw, field.kind, amountCurrency),
    });
  }

  if (!changes.length) {
    return {
      type: HISTORY_EVENT_TYPES.RECORD_UPDATED,
      summary: 'Demand record saved (no field changes)',
      changes,
    };
  }

  const onlyEvidence =
    changes.length === 1 && changes[0].key === 'Evidence_Vault_URL';
  const type = onlyEvidence
    ? HISTORY_EVENT_TYPES.EVIDENCE_UPDATED
    : HISTORY_EVENT_TYPES.RECORD_UPDATED;

  const detailed = changes.slice(0, 4).map((change) => {
    if (change.key === 'Evidence_Vault_URL') {
      return `evidence (${change.from} → ${change.to})`;
    }
    return `${change.label} (${change.from} → ${change.to})`;
  });
  const extra = changes.length - detailed.length;
  if (extra > 0) detailed.push(`${extra} more field${extra === 1 ? '' : 's'}`);

  const summary = onlyEvidence
    ? `Updated evidence (${changes[0].from} → ${changes[0].to})`
    : `Updated ${joinList(detailed)}`;

  return { type, summary, changes };
}

export function createHistoryEvent({
  matterId,
  type,
  summary,
  actorName = '',
  actorRole = 'SYSTEM',
  createdAt = new Date().toISOString(),
  id = '',
} = {}) {
  return {
    ROWID: id || `mev_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    Matter_ID: String(matterId || ''),
    Event_Type: String(type || HISTORY_EVENT_TYPES.RECORD_UPDATED),
    Summary: String(summary || '').trim() || 'Activity recorded',
    Actor_Name: String(actorName || '').trim() || null,
    Actor_Role: String(actorRole || 'SYSTEM').toUpperCase(),
    Created_At: createdAt || new Date().toISOString(),
  };
}

export function getLocalMatterHistory(matterId) {
  const all = readAll();
  const list = Array.isArray(all[String(matterId)]) ? [...all[String(matterId)]] : [];
  return sortHistory(list);
}

export function setLocalMatterHistory(matterId, events) {
  const all = readAll();
  all[String(matterId)] = sortHistory(events || []);
  writeAll(all);
  return all[String(matterId)];
}

export function appendLocalMatterHistory(matterId, event) {
  const list = getLocalMatterHistory(matterId);
  const next = sortHistory([...list, event]);
  return setLocalMatterHistory(matterId, next);
}

export function sortHistory(events = []) {
  return [...(events || [])].sort(
    (a, b) => new Date(a.Created_At || 0).getTime() - new Date(b.Created_At || 0).getTime()
  );
}

/** Seed a Created event when a demand has no history yet. */
export function ensureCreatedHistory(matter, { actorName = '', actorRole = 'CLIENT' } = {}) {
  if (!matter?.ROWID) return [];
  const matterId = String(matter.ROWID);
  const existing = getLocalMatterHistory(matterId);
  if (existing.length) return existing;
  const created = createHistoryEvent({
    matterId,
    type: HISTORY_EVENT_TYPES.CREATED,
    summary: 'Demand created',
    actorName,
    actorRole,
    createdAt: matter.Created_At || new Date().toISOString(),
  });
  return setLocalMatterHistory(matterId, [created]);
}

export function mergeHistoryLists(...lists) {
  const byId = new Map();
  for (const list of lists) {
    for (const event of list || []) {
      const id = String(event?.ROWID || '');
      if (!id) continue;
      if (!byId.has(id)) byId.set(id, event);
    }
  }
  return sortHistory(Array.from(byId.values()));
}

export function historyTypeLabel(type) {
  switch (String(type || '').toUpperCase()) {
    case HISTORY_EVENT_TYPES.CREATED:
      return 'Created';
    case HISTORY_EVENT_TYPES.RECORD_UPDATED:
      return 'Updated';
    case HISTORY_EVENT_TYPES.EVIDENCE_UPDATED:
      return 'Evidence';
    case HISTORY_EVENT_TYPES.MESSAGE_SENT:
      return 'Message';
    case HISTORY_EVENT_TYPES.MESSAGE_DELETED:
      return 'Message deleted';
    case HISTORY_EVENT_TYPES.ARCHIVED:
      return 'Archived';
    case HISTORY_EVENT_TYPES.RESTORED:
      return 'Restored';
    case HISTORY_EVENT_TYPES.STATUS_CHANGED:
      return 'Status';
    case HISTORY_EVENT_TYPES.ATTORNEY_ASSIGNED:
      return 'Assigned';
    default:
      return 'Activity';
  }
}
